import { useMutation } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import apiV2 from "api-v2";
import { useInvalidateMfaStatus } from "./useMfaStatus";
import type { RecoveryCodesResource } from "../types/Mfa.type";

// `expectedErrors` is a custom escape hatch read by the api-v2 response
// interceptor (src/api-v2.js) to silence its default error-toast for listed
// statuses — not a real axios config field, so it's typed separately here to
// avoid the excess-property check on an inline literal.
interface ApiV2RequestConfig extends AxiosRequestConfig {
	expectedErrors?: number[];
}

export interface TotpConfirmPayload {
	code: string;
}

// Re-exported for existing call sites; the real definition lives in
// `Mfa.type.ts` (PRO-625 Task 009 — de-duplicated, was a divergence-prone
// copy of `RecoveryCodesResource`).
export type RecoveryCodesResourceShape = RecoveryCodesResource;

export class MfaAlreadyEnabledError extends Error {}

/**
 * Laravel's validation-error bag values are always `string[]` (one entry per
 * failed rule), never a bare `string` — normalize defensively in case a
 * caller/mock supplies the singular shape (PRO-625 Task 009 hardening item).
 */
const firstErrorMessage = (value: string | string[] | undefined): string | undefined =>
	Array.isArray(value) ? value[0] : value;

const confirmTotp = async (
	payload: TotpConfirmPayload
): Promise<RecoveryCodesResourceShape> => {
	try {
		const config: ApiV2RequestConfig = { expectedErrors: [422] };
		const response = await apiV2.post("/mfa/totp/confirm", payload, config);
		return (response.data as { data?: RecoveryCodesResourceShape })?.data ?? response.data;
	} catch (error) {
		const data = (error as {
			response?: {
				data?: {
					error_code?: string;
					errors?: { two_factor?: string | string[] };
				};
			};
		})?.response?.data;

		if (data?.error_code === "TWO_FACTOR_ALREADY_ENABLED") {
			const message = firstErrorMessage(data.errors?.two_factor) ?? "Two-factor authentication is already enabled.";
			throw new MfaAlreadyEnabledError(message);
		}

		throw error;
	}
};

export const useTotpConfirm = () => {
	const invalidateMfaStatus = useInvalidateMfaStatus();

	return useMutation<RecoveryCodesResourceShape, Error, TotpConfirmPayload>({
		mutationFn: confirmTotp,
		onSuccess: () => {
			invalidateMfaStatus();
		},
		onError: (error) => {
			if (error instanceof MfaAlreadyEnabledError) {
				invalidateMfaStatus();
			}
		},
	});
};
